import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Upload } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { TRADES, FREELANCER_BENEFITS } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPERIENCE = ['Less than 2 years', '3–5 years', '6–10 years', '10+ years'];
const CERTS = ['Yes — fully certified and insured', 'Certified but no insurance currently', 'Working towards certification', 'No formal certification yet'];
const START = ['Immediately', 'Within 1–2 weeks', 'Within 1 month', 'Within 2–3 months'];
const TRADE_OPTS = ['Plumber', 'Electrician', 'Painter & Decorator', 'House Cleaner', 'Commercial Cleaner', 'HVAC Technician', 'Multiple Trades'];

const JoinTeam = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cv, setCv] = useState(null);
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', location: '', trade: '', experience: '', certification: '', about: '', start: '',
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectTrade = (role) => {
    update('trade', role);
    document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Explicit validation with clear feedback (mobile-friendly)
    const required = [
      ['fullName', 'Full Name'],
      ['email', 'Email'],
      ['trade', 'Trade / Service'],
      ['about', 'About your experience'],
    ];
    for (const [key, label] of required) {
      if (!form[key] || !String(form[key]).trim()) {
        toast({ title: `${label} is required`, description: `Please fill in the "${label}" field to continue.`, variant: 'destructive' });
        document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast({ title: 'Invalid email', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    if (!agree) {
      toast({ title: 'Please accept the Terms', description: 'You must agree to the Freelancer Terms & Conditions to continue.', variant: 'destructive' });
      document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    const postWithRetry = async (url, data, config) => {
      let lastErr = null;
      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          return await axios.post(url, data, config);
        } catch (err) {
          lastErr = err;
          if (err?.response) throw err; // real server error, don't retry
          await new Promise((r) => setTimeout(r, 1200 * attempt));
        }
      }
      throw lastErr;
    };

    try {
      // 1) Submit application data (small & fast — always goes through)
      const res = await postWithRetry(`${API}/applications`, { ...form, agreedTerms: true }, { timeout: 30000 });
      const appId = res?.data?.id;
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // 2) Upload files as a best-effort follow-up, sent as JSON/base64 (more reliable on mobile than multipart)
      const hasFiles = cv || docs.length > 0;
      if (appId && hasFiles) {
        try {
          const toB64 = (file) => new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve({ filename: file.name, content_type: file.type || 'application/octet-stream', data: String(r.result).split(',')[1] });
            r.onerror = reject;
            r.readAsDataURL(file);
          });
          const fileObjs = [];
          if (cv) fileObjs.push(await toB64(cv));
          for (const d of docs) fileObjs.push(await toB64(d));
          await postWithRetry(`${API}/applications/${appId}/files-json`, { files: fileObjs }, { timeout: 180000 });
        } catch (fileErr) {
          toast({
            title: 'Application received — files not uploaded',
            description: 'Your application was submitted, but the file upload failed (large file or weak signal). Please email your documents to general@ealeadsolutions.co.uk.',
          });
        }
      }
    } catch (err) {
      const isNetwork = !err?.response;
      toast({
        title: 'Something went wrong',
        description: err?.response?.data?.detail
          || (isNetwork
            ? 'Network issue — please check your connection and try again. If it keeps failing, reload the page or contact us on WhatsApp.'
            : 'Please try again or contact us on WhatsApp.'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 outline-none transition-colors focus:border-[#3a9b5c] focus:ring-2 focus:ring-[#3a9b5c]/20';
  const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700';

  if (submitted) {
    return (
      <section className="bg-[#e9f3ec] py-24">
        <div className="mx-auto max-w-xl px-5 text-center">
          <div className="rounded-2xl bg-white p-10 shadow-md">
            <CheckCircle2 className="mx-auto h-16 w-16 text-[#3a9b5c]" />
            <h1 className="ea-heading mt-5 text-2xl font-bold">Application Submitted!</h1>
            <p className="mt-3 text-gray-600">Thank you for applying. We review every application within 48 hours and will be in touch soon.</p>
            <Link to="/" className="mt-7 inline-block rounded-full bg-[#3a9b5c] px-7 py-3 font-semibold text-white transition-colors hover:bg-[#318150]">Back to Home</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div>
      <section className="ea-hero-gradient py-20">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <span className="inline-block rounded-full bg-white/15 px-4 py-1.5 text-sm font-medium text-white">Now Recruiting Across London</span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">You have the skills, we have the customers.</h1>
          <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-white/85">
            Join E.A. Lead Solutions as a vetted professional. We send you the jobs, handle the bookings, and you focus on what you do best.
          </p>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="text-center">
            <h2 className="ea-heading text-3xl font-bold">Choose Your Trade</h2>
            <p className="mt-3 text-gray-500">Select the service you specialise in. We’re looking for experienced, reliable professionals.</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TRADES.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => selectTrade(t.role)} type="button" className="ea-card-hover rounded-2xl border border-gray-100 bg-white p-7 text-left shadow-sm">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#e9f3ec]">
                    <Icon className="h-7 w-7 text-[#3a9b5c]" />
                  </div>
                  <p className="mt-5 text-xs font-medium uppercase tracking-wide text-gray-400">{t.join}</p>
                  <h3 className="font-display text-xl font-bold text-[#1b4f72]">{t.role}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{t.text}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section id="apply-form" className="bg-[#e9f3ec] py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="text-center">
            <h2 className="ea-heading text-3xl font-bold">Register as a Professional</h2>
            <p className="mt-3 text-gray-500">Fill in your details below. We review every application within 48 hours.</p>
          </div>
          <form onSubmit={handleSubmit} className="mt-10 space-y-5 rounded-2xl bg-white p-8 shadow-md">
            <div className="grid gap-5 sm:grid-cols-2">
              <div><label className={labelCls}>Full Name *</label><input className={inputCls} value={form.fullName} onChange={(e) => update('fullName', e.target.value)} /></div>
              <div><label className={labelCls}>Email *</label><input type="email" className={inputCls} value={form.email} onChange={(e) => update('email', e.target.value)} /></div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div><label className={labelCls}>Phone</label><input className={inputCls} value={form.phone} onChange={(e) => update('phone', e.target.value)} /></div>
              <div><label className={labelCls}>Location / Area</label><input className={inputCls} value={form.location} onChange={(e) => update('location', e.target.value)} /></div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Trade / Service *</label>
                <select className={inputCls} value={form.trade} onChange={(e) => update('trade', e.target.value)}>
                  <option value="">Select your trade...</option>
                  {TRADE_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Years of Experience</label>
                <select className={inputCls} value={form.experience} onChange={(e) => update('experience', e.target.value)}>
                  <option value="">Select...</option>
                  {EXPERIENCE.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Certifications / Insurance</label>
              <select className={inputCls} value={form.certification} onChange={(e) => update('certification', e.target.value)}>
                <option value="">Select...</option>
                {CERTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Upload CV (optional)</label>
                <label className="flex cursor-pointer items-center gap-2 overflow-hidden rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 hover:border-[#3a9b5c]"><Upload size={16} className="shrink-0" /> <span className="truncate">{cv ? cv.name : 'Upload CV'}</span><input type="file" className="hidden" onChange={(e) => setCv(e.target.files[0] || null)} /></label>
              </div>
              <div>
                <label className={labelCls}>Certificates / Docs (optional)</label>
                <label className="flex cursor-pointer items-center gap-2 overflow-hidden rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 hover:border-[#3a9b5c]"><Upload size={16} className="shrink-0" /> <span className="truncate">{docs.length > 0 ? docs.map((d) => d.name).join(', ') : 'Upload Docs'}</span><input type="file" multiple className="hidden" onChange={(e) => setDocs(Array.from(e.target.files))} /></label>
              </div>
            </div>
            <div><label className={labelCls}>About your experience *</label><textarea rows={4} className={inputCls} value={form.about} onChange={(e) => update('about', e.target.value)} /></div>
            <div>
              <label className={labelCls}>When can you start?</label>
              <select className={inputCls} value={form.start} onChange={(e) => update('start', e.target.value)}>
                <option value="">Select...</option>
                {START.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <label className="flex items-start gap-3 rounded-lg bg-[#e9f3ec] p-4">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#3a9b5c]" />
              <span className="text-sm text-gray-700">
                I have read and agree to the <a href="/freelancer-terms?ref=join-form" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#3a9b5c] underline">Freelancer Terms & Conditions</a> *
              </span>
            </label>

            <button type="submit" disabled={submitting} className="w-full rounded-full bg-[#3a9b5c] px-6 py-3.5 font-semibold text-white shadow-md transition-all hover:bg-[#318150] disabled:opacity-60">
              {submitting ? 'Submitting...' : 'Submit My Application →'}
            </button>
          </form>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="ea-heading text-center text-3xl font-bold">Why Join E.A. Lead Solutions?</h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {FREELANCER_BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="rounded-2xl border border-gray-100 bg-white p-7 text-center shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-[#e9f3ec]"><Icon className="h-7 w-7 text-[#3a9b5c]" /></div>
                  <h3 className="mt-5 font-display text-base font-bold text-[#1b4f72]">{b.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default JoinTeam;
